import React, { useEffect, useRef } from 'react'
import Lightbox, {
    NextIcon,
    PreviousIcon,
    useController,
    useLightboxState,
    useNavigationState,
} from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import { resolveDocumentTheme } from 'constants/theme'
import { registerOpenLightbox } from 'state/openLightboxes'
import styles from './lightbox.module.scss'
import 'yet-another-react-lightbox/styles.css'

function BottomNavigation({ isDark }) {
    const { slides, currentIndex } = useLightboxState()
    const { prev, next } = useController()
    const { prevDisabled, nextDisabled } = useNavigationState()

    const navigate = callback => event => {
        event.preventDefault()
        event.stopPropagation()
        callback()
    }

    return (
        <div
            className={`${styles.bottomNavigation} ${isDark ? styles.bottomNavigationDark : ''}`}
            data-testid="payload-lightbox-navigation"
            onClick={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
        >
            <button
                type="button"
                className={styles.bottomNavigationButton}
                data-testid="payload-lightbox-previous"
                aria-label="Previous image"
                disabled={prevDisabled}
                onClick={navigate(prev)}
            >
                <PreviousIcon aria-hidden="true" />
            </button>
            <span
                className={styles.bottomNavigationCount}
                data-testid="payload-lightbox-count"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {currentIndex + 1} / {slides.length}
            </span>
            <button
                type="button"
                className={styles.bottomNavigationButton}
                data-testid="payload-lightbox-next"
                aria-label="Next image"
                disabled={nextDisabled}
                onClick={navigate(next)}
            >
                <NextIcon aria-hidden="true" />
            </button>
        </div>
    )
}

/**
 * Get the current theme from the document
 * @returns {'light' | 'dark'}
 */
function getCurrentTheme() {
    const dataTheme = document.documentElement?.getAttribute('data-theme')
    const systemPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches

    return resolveDocumentTheme(dataTheme, systemPrefersDark)
}

/**
 * App-wide lightbox component using yet-another-react-lightbox (YARL)
 *
 * Features:
 * - Gallery navigation (left/right arrows) - hidden for single images
 * - Pinch-to-zoom on mobile (no visible zoom controls)
 * - Keyboard navigation (arrow keys, Escape to close)
 * - Swipe gestures on mobile
 * - Theme-aware background color
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the lightbox is open
 * @param {() => void} props.onClose - Callback when lightbox should close
 * @param {Array<{src: string}>} props.slides - Array of slide objects with src property
 * @param {number} [props.index=0] - Index of slide to show initially
 * @param {boolean} [props.bottomNavigation=false] - Show a bottom gallery counter and arrows
 */
export default function AppLightbox({
    open,
    onClose,
    slides,
    index = 0,
    bottomNavigation = false,
}) {
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    const isShown = Boolean(open && slides?.length)

    // Back (Android hardware back) closes the maximized image instead of swiping panels.
    useEffect(() => {
        if (!isShown) return undefined
        return registerOpenLightbox(() => onCloseRef.current?.())
    }, [isShown])

    if (!slides || slides.length === 0) {
        return null
    }

    const isSingleImage = slides.length === 1
    const showBottomNavigation = bottomNavigation && !isSingleImage
    const theme = getCurrentTheme()

    // Theme-aware colors
    const isDark = theme === 'dark'
    // Background: Light mode white, Dark mode dark gray
    const backgroundColor = isDark
        ? 'rgba(4, 6, 11, 0.95)' // dark void (desktop-bg) with slight transparency
        : 'rgba(255, 255, 255, 0.95)' // white with slight transparency
    // Loading/button icons: need contrast against background
    const iconColor = isDark
        ? 'rgba(255, 255, 255, 0.8)' // light icons on dark bg
        : 'rgba(55, 65, 81, 0.8)' // dark icons on light bg ($gray-700)

    const stopPropagation = e => {
        e.stopPropagation()
    }

    return (
        // Wrapper div stops click events from propagating to panels underneath
        <div onClick={stopPropagation} onMouseDown={stopPropagation}>
            <Lightbox
                open={open}
                close={onClose}
                slides={slides}
                index={index}
                plugins={[Zoom]}
                animation={{ fade: 300 }}
                carousel={{
                    finite: isSingleImage,
                }}
                controller={{
                    closeOnBackdropClick: true,
                }}
                zoom={{
                    maxZoomPixelRatio: 3,
                    scrollToZoom: true,
                }}
                render={{
                    // Payload galleries use the compact bottom controls instead of side arrows.
                    buttonPrev: isSingleImage || showBottomNavigation ? () => null : undefined,
                    buttonNext: isSingleImage || showBottomNavigation ? () => null : undefined,
                    // Hide zoom buttons (keep pinch-to-zoom functionality)
                    buttonZoom: () => null,
                    controls: showBottomNavigation
                        ? () => <BottomNavigation isDark={isDark} />
                        : undefined,
                }}
                styles={{
                    container: {
                        backgroundColor,
                        boxSizing: 'border-box',
                        paddingTop: 'var(--modal-safe-area-top)',
                        paddingRight: 'var(--modal-safe-area-right)',
                        paddingBottom: showBottomNavigation
                            ? 'calc(72px + var(--modal-safe-area-bottom))'
                            : 'var(--modal-safe-area-bottom)',
                        paddingLeft: 'var(--modal-safe-area-left)',
                    },
                    toolbar: {
                        paddingTop: 'calc(8px + var(--modal-safe-area-top))',
                        paddingRight: 'calc(8px + var(--modal-safe-area-right))',
                    },
                    // Set CSS custom properties for YARL's loading icon
                    root: {
                        '--yarl__slide_icon_loading_color': iconColor,
                        '--yarl__color_button': iconColor,
                        '--yarl__color_button_active': isDark ? '#fff' : '#111827',
                    },
                }}
                // Only show close button, hide zoom controls
                toolbar={{
                    buttons: ['close'],
                }}
            />
        </div>
    )
}
